<script lang="ts" setup>
import { SliceZone, usePrismic } from '@prismicio/vue';
import { ref } from 'vue';
import { components } from '@/slices';
import useNetwork from '@/composables/useNetwork';
import IconUntangledFullDark from '@/components/icons/IconUntangledFullDark.vue';
import IconUntangledFullLight from '@/components/icons/IconUntangledFullLight.vue';
import RiskDisclaimerModal from '../modals/RiskDisclaimerModal.vue';

const { networkSlug } = useNetwork();

const { client } = usePrismic();
const leftSlices = ref();
const rightSlices = ref();

const showRiskModal = ref(false);

watchEffect(async () => {
  try {
    const { data } = await client.getByUID('footer', 'footer');

    leftSlices.value = [data.slices[0]];
    rightSlices.value = [data.slices[1]];
  } catch (err) {
    console.log(err);
  }
});
</script>

<template>
  <footer>
    <div
      class="xl:container lg:px-4 xl:mx-auto dark:border-t dark:border-gray-800"
    >
      <div
        class="flex flex-col md:flex-row gap-8 md:justify-between py-12 px-4 lg:px-0 text-primary"
      >
        <div class="flex flex-col gap-8 justify-between">
          <router-link
            class="h-8 font-medium link"
            :to="{ name: 'home', params: { networkSlug } }"
          >
            <div class="hidden dark:block">
              <IconUntangledFullDark />
            </div>

            <div class="dark:hidden">
              <IconUntangledFullLight />
            </div>
          </router-link>

          <div class="flex md:hidden flex-col gap-2">
            <p>
              <router-link
                class="text-lg font-medium link"
                :to="{ name: 'home', params: { networkSlug } }"
              >
                {{ $t('pool') }}
              </router-link>
            </p>
            <p>
              <router-link
                class="text-lg font-medium link"
                :to="{ name: 'portfolio', params: { networkSlug } }"
              >
                {{ $t('portfolio') }}
              </router-link>
            </p>
          </div>
          <SliceZone
            v-if="leftSlices"
            :slices="leftSlices"
            :components="components"
          />
        </div>
        <div class="flex flex-col gap-8 justify-between">
          <div class="md:order-2">
            <div class="flex lg:flex-col md:justify-end">
              <SliceZone
                v-if="rightSlices"
                :slices="rightSlices"
                :components="components"
              />
            </div>
          </div>

          <div
            class="flex flex-wrap md:order-3 gap-x-3 lg:gap-x-4 gap-y-2 md:justify-end"
          >
            <p>
              <BalLink
                href="https://www.untangled.finance/terms"
                noStyle
                class="text-sm text-center text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-yellow-500"
                external
              >
                {{ $t('policies.termsOfUse') }}
              </BalLink>
            </p>
            <p>
              <BalLink
                href="https://www.untangled.finance/privacy"
                noStyle
                class="text-sm text-center text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-yellow-500"
                external
              >
                {{ $t('policies.privacyPolicy') }}
              </BalLink>
            </p>
            <p>
              <BalLink
                noStyle
                class="text-sm text-center text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-yellow-500"
                external
                @click="() => (showRiskModal = true)"
              >
                Risk Disclaimer
              </BalLink>
            </p>
          </div>
        </div>
      </div>
    </div>
    <RiskDisclaimerModal
      :show="showRiskModal"
      :onClose="() => (showRiskModal = false)"
    />
  </footer>
</template>

<style scoped>
footer {
  @apply bg-gray-50 dark:bg-gray-900;
}

footer :deep(.logotype) {
  @apply origin-top-left;

  transform: scale(1.5);
}

.link {
  @apply text-black dark:text-white transition-colors flex items-center no-underline hover:text-purple-600;
}

.link:hover,
.link:focus-visible {
  @apply text-purple-600 dark:text-yellow-500 no-underline;
}

.link:focus:not(:focus-visible) {
  outline: none;
}
</style>
